import { useContext, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { onSubmit } from ".";
import { ApiContext } from "../../context/ApiContext";
import { ModalContext } from "../../context/ModalContext";
import endpoints from "../../data/endpoints";
import useStepData from "../../hooks/useStepData";
import apiRequest from "../../requests/apiRequest";
import Button from "../../shared/Button";
import ErrorModal from "../../shared/ErrorModal";
import InfoNote from "../../shared/InfoNote";
import Loader from "../../shared/Loader";
import ContentModal from "../../shared/Modal";
import Timeline from "../../shared/Timeline";
import { FormInputs } from "../../types/types";
import __ from "../../utils/getContent";
import CheckboxField from "../fields/checkboxField";
import DateField from "../fields/dateField";
import EmailField from "../fields/emailField";
import MemberReferralField from "../fields/memberReferralField";
import PasswordField from "../fields/passwordField";
import RadioGroupField from "../fields/radioGroupField";
import SelectField from "../fields/selectField";
import TelField from "../fields/telField";
import TextField from "../fields/textField";
import ZipField from "../fields/zipcodeField";

const StepDetails = (): React.JSX.Element => {
	const {
		handleSubmit,
		getValues,
		trigger,
		watch,
		setError,
		clearErrors,
		formState: { errors }, // eslint-disable-line
	} = useFormContext<FormInputs>();

	const [zipIsValidating, setZipIsValidating] = useState(false);
	const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
	const { error, loading, stepData } = useStepData(4);
	const { data, setData } = useContext(ApiContext);
	const [isLoading, setIsLoading] = useState(false);
	const [submitErrors, setSubmitErrors] = useState<string[]>([]);
	const [submissionError, setSubmissionError] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);
	const { setIsModalOpen } = useContext(ModalContext);
	const navigate = useNavigate();

	// Pluck the member email address from the form.
	const memberEmail = getValues("member.emailAddress") || "";

	useEffect(() => {
		setIsModalOpen(isTermsModalOpen);
	}, [isTermsModalOpen, setIsModalOpen]);

	const selectedLanguage = watch("member.contractLanguage");

	useEffect(() => {
		if (selectedLanguage) {
			setData({
				...data,
				language: selectedLanguage,
			});
		}
	}, [selectedLanguage, setData]); // eslint-disable-line react-hooks/exhaustive-deps

	// Check if guardian signature is required
	const memberDOB = watch("member.dateOfBirth");

	useEffect(() => {
		if (memberDOB) {
			const dob = new Date(memberDOB);
			const age = new Date().getFullYear() - dob.getFullYear();
			if (age < data?.selectedMembership?.MinAge) {
				setData({
					...data,
					requireGuardianSignature: true,
				});
			} else {
				setData({
					...data,
					requireGuardianSignature: false,
				});
			}
		}
	}, [memberDOB, setData]); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * Furm submit.
	 */
	const doSubmit = async () => {
		setIsLoading(true);
		createMember();
	};

	/**
	 * Create a new member function.
	 * @returns
	 */
	const createMember = async () => {
		const errorArray = [];

		// Check if all members have unique emails.
		const duplicates = checkForDuplicateEmails();
		if (duplicates?.length) {
			const duplicateMsg = __(
				"input_email_description",
				"All members must have a unique email address.",
			);

			// Set error for each duplicate email.
			duplicates.forEach((index: any) => {
				setError(`submembers.${index}.email`, {
					type: "custom",
					message: duplicateMsg,
				});
			});

			errorArray.push([duplicateMsg]);
		}

		// Check if the zip code is valid.
		const zipId = data?.zipCodeInfo?.ZipID;
		if (!zipId) {
			setError("member.zip", {
				type: "custom",
				message: __("invalid_zip", "Please enter a valid zip code."),
			});
			errorArray.push([__("invalid_zip", "Please enter a valid zip code.")]);
		}

		// Check if passwords match.
		if (getValues("login.password") !== getValues("login.passwordConfirm")) {
			setError("login.password", {
				type: "custom",
				message: __(
					"password_mismatch",
					"The passwords you have entered do not match.",
				),
			});

			errorArray.push([
				__("password_mismatch", "The passwords you have entered do not match."),
			]);
		}

		// Exit if there are any errors.
		if (errorArray?.length) {
			setSubmissionError(true);

			formRef?.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});

			setTimeout(() => {
				formRef?.current?.focus({
					preventScroll: true,
				});
			}, 1000);

			setIsLoading(false);
			return;
		}

		setSubmissionError(false);

		const emergencyContactName = `${getValues(
			"emergencyContact.firstName",
		)} ${getValues("emergencyContact.lastName")}`;

		const gardianSignature = `${getValues(
			"gardianSignature.firstName",
		)} ${getValues("gardianSignature.lastName")}`;

		const requestBody = [
			{ firstname: getValues("member.firstName") },
			{ lastname: getValues("member.lastName") },
			{ email: memberEmail },
			{ username: getValues("login.userName") },
			{ password: getValues("login.password") },
			{ dateOfBirth: getValues("member.dateOfBirth") },
			{ gender: getValues("member.gender") },
			{ mobilenumber: getValues("member.mobilePhone") },
			{ emergencycontact: emergencyContactName },
			{ emergencynumber: getValues("emergencyContact.mobilePhone") },
			{ clubId: data?.get_club_id },
			{ membershiptypeid: data?.selectedMembership?.MembershipTypeId },
			{ applysplitifee: data?.splitPayment ? "true" : "false" },
			{ guardianSignature: gardianSignature },
			{ referral: data?.referralName },
			{ language: data?.language ?? "en" },
			{ address1: getValues("member.address1") },
			{ address2: getValues("member.address2") },
			{ csizipid: zipId },
			...getFormattedPackageIDs(),
			...getFormattedSubMembers(),
		];

		if (data.EmployeeAuthTicket) {
			requestBody.push({ EmployeeAuthTicket: data.EmployeeAuthTicket });
		}

		const response = await apiRequest("createMember", requestBody);
		const errors = getResponseErrors(response) as string[];

		if (errors) {
			setSubmitErrors(errors);
			setIsLoading(false);
		} else {
			const { PaymentGatewayURL } = response?.data;

			if (!PaymentGatewayURL) {
				setSubmitErrors(["An error has occured. Please try again."]);
				setIsLoading(false);
			} else {
				setData({
					...data,
					...response.data,
					firstname: getValues("member.firstName"),
					lastname: getValues("member.lastName"),
					email: memberEmail,
					username: getValues("login.userName"),
					password: getValues("login.password"),
					dateOfBirth: getValues("member.dateOfBirth"),
					gender: getValues("member.gender"),
					mobilenumber: getValues("member.mobilePhone"),
					emergencycontact: emergencyContactName,
					emergencynumber: getValues("emergencyContact.mobilePhone"),
					guardianSignature: gardianSignature,
					address1: getValues("member.address1"),
					address2: getValues("member.address2"),
					csizipid: zipId,
				});

				navigate("?step=payment");
			}
		}
	};

	const getResponseErrors = (response: any) => {
		let errors = [];

		if (response?.message?.Error) {
			errors.push(response.message.Error);
		}

		const modelStateErrors = response?.message?.ModelState;
		if (modelStateErrors) {
			errors = [...errors, ...Object.values(modelStateErrors)];
		}

		return errors.length ? errors : false;
	};

	const getFormattedSubMembers = () => {
		const subMembers = getValues("submembers") ?? [];

		const membersFormatted = subMembers.flatMap((submember, index) =>
			Object.entries(submember).map(([key, value]) => ({
				[`submembers[${index}][${key}]`]: value,
			})),
		);

		return membersFormatted;
	};

	const getFormattedPackageIDs = () => {
		const packageIDs = data?.startstrongpackageids ?? [];

		const packageIDsFormatted = packageIDs.map(
			(packageID: any, index: any) => ({
				[`startstrongpackageids[${index}]`]: packageID,
			}),
		);

		return packageIDsFormatted ?? [];
	};

	if (loading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<Loader colorCircle="#000" colorRotator="#ddd" />
			</div>
		);
	}

	if (error) {
		navigate("?step=contact&error=1");
	}

	// Get maximum Child submember age. Return false if N/A.
	const ageMaxChild: string | boolean =
		data?.get_submember_age_information?.SubmemberAgeInfo?.Ages.find(
			(item: any) => item?.Type === "Children",
		)?.Age.toString() || false;

	// Get minimum Adult submember age. Return false if N/A.
	const ageMinAdult: string | boolean =
		data?.get_submember_age_information?.SubmemberAgeInfo?.Ages.find(
			(item: any) => item?.Type === "Adults",
		)?.Age.toString() || false;

	// Construct the message for max/min member ages.
	let submemberAgeMessage = "";
	if (ageMinAdult) {
		submemberAgeMessage += `all adult members must be over ${ageMinAdult}`;
	}
	if (ageMaxChild) {
		if (submemberAgeMessage) {
			submemberAgeMessage += " and ";
		}
		submemberAgeMessage += `all child members must be under ${ageMaxChild}`;
	}

	/**
	 * Detect duplicate email addresses in form submission.
	 */
	const checkForDuplicateEmails = () => {
		const subMembers = getValues("submembers") ?? [];
		if (!subMembers) {
			return []; // No submembers to check, return true as email is unique.
		}

		const duplicates: any = [];

		// Filter for matching email addresses.
		subMembers.forEach((member: any, index: Number) => {
			if (member?.email === memberEmail) {
				duplicates.push(index);
			}
		});

		// Return true if no duplicates found.
		return duplicates;
	};

	const handleReferralSelection = (selection: string) => {
		setData({
			...data,
			...{ referralName: selection },
		});
	};

	const handleZipChange = async () => {
		setData({
			...data,
			zipCodeInfo: [],
		});

		const zip = getValues("member.zip");
		if (zip.length < 5) return __("input_required", "This field is required.");

		try {
			setZipIsValidating(true);
			const headers = {
				"Content-Type": "application/json",
				...(window?.midtownMembershipsVerify?.nonce_verification_enabled && {
					"X-WP-Nonce": window?.midtownMembershipsVerify?.nonce || "",
				}),
			};
			const response = await fetch(endpoints.validateZip.url, {
				method: endpoints.validateZip.requestMethod,
				headers,
				credentials: "same-origin",
				body: JSON.stringify({ zip }),
			});

			if (!response.ok) {
				return __("zip_code_invalid_message", "Zip validation failed.");
			}

			const result = await response.json();
			const { Zip_Code_Information: zipcode = [] } = result?.data;

			// Check if the zipcode is valid, set custom validation message if not.
			if (!zipcode?.length) {
				setError("member.zip", {
					type: "custom",
					message: __("invalid_zip", "Please enter a valid zip code."),
				});
				return;
			} else {
				clearErrors("member.zip");
			}

			setData({
				...data,
				zipCodeInfo: zipcode[0],
			});
		} catch (error) {
			console.error("Zip validation error:", error);
		} finally {
			setZipIsValidating(false);
		}
	};

	return (
		<>
			<Timeline currentStep={4} />

			<ErrorModal
				errors={submitErrors.flat()}
				title={__("submission_errors_title", "Form Error")}
				closeModal={() => {
					setSubmitErrors([]);
				}}
			/>

			<form
				ref={formRef}
				onSubmit={handleSubmit(doSubmit)}
				className="flex flex-col items-center"
				z-index={-1}
			>
				<h1 className="mb-1 text-3xl font-bold uppercase">
					{__("member_info", "Member Info")}
				</h1>

				{data?.selectedClub?.Name && (
					<h2 className="location-name mb-2 text-lg font-bold">
						{data.selectedClub.Name}
					</h2>
				)}

				<p className="mb-6 text-xs">
					{__("required_fields_instructions", "Required Fields")}
				</p>

				{!!submissionError && (
					<div className="mb-5 w-full max-w-96 px-3 pb-2 pt-3.5 text-sm text-red-500">
						{__(
							"step_submission_errors",
							"There were errors with your submission. Resolve the issues and try submitting the form again.",
						)}
					</div>
				)}

				<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
					<h2 className="section-heading mb-5 text-lg">
						{__("main_adult", "Main Adult")}
					</h2>

					<TextField
						registeredName="member.firstName"
						label={__("input_first_name_label", "First Name")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						placeholder={__("input_first_name_label", "First Name")}
						defaultValue={getValues("member.firstName") || ""}
						disabled={!!data.contact_id}
					/>

					<TextField
						registeredName="member.lastName"
						label={__("input_last_name_label", "Last Name")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						placeholder={__("input_last_name_label", "Last Name")}
						defaultValue={getValues("member.lastName") || ""}
						disabled={!!data.contact_id}
					/>

					<EmailField
						registeredName="member.emailAddress"
						label={__("input_email_label", "Email")}
						description={__(
							"input_email_description",
							"all members must have a unique email address",
						)}
						requiredText={__(
							"email_required",
							"Please enter a valid email address.",
						)}
						isRequired={true}
						placeholder={__("input_email_label", "Email")}
						defaultValue={memberEmail || ""}
						disabled={!!data.contact_id}
					/>

					<TelField
						registeredName="member.mobilePhone"
						label={__("input_mobile_phone_label", "Mobile Phone")}
						isRequired={true}
						requiredText={__(
							"phone_required",
							"Please enter a valid phone number.",
						)}
						placeholder={__("input_mobile_phone_label", "Mobile Phone")}
						defaultValue={getValues("member.mobilePhone") || ""}
						disabled={!!data.contact_id}
					/>

					<DateField
						registeredName="member.dateOfBirth"
						label={__("input_dob_label", "DOB")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						placeholder={__("input_dob_placeholder", "1990-12-12")}
					/>

					{loading ? (
						<Loader colorCircle="#000" colorRotator="#ddd" />
					) : (
						<RadioGroupField
							registeredName="member.gender"
							label={__("input_gender_label", "Gender")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
							radios={stepData?.get_genders?.Genders.map(
								(gender: string, index: Number) => ({
									id: `${gender}-${index}`.toString(),
									label: gender,
									value: gender,
								}),
							)}
						/>
					)}

					<TextField
						registeredName="member.address1"
						label={__("input_field_line_1_label", "Address Line 1")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						placeholder={__("input_field_line_1_label", "Address Line 1")}
					/>

					<TextField
						registeredName="member.address2"
						label={__("input_field_line_2_label", "Address Line 2")}
						isRequired={false}
						placeholder={__("input_field_line_2_label", "Address Line 2")}
					/>

					<ZipField
						registeredName="member.zip"
						label={__("input_zip_code_label", "Zip Code")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						placeholder={__("input_zip_code_label", "Zip Code")}
						maxLength={7}
						isValidating={zipIsValidating}
						onBlur={async () => {
							handleZipChange();
						}}
						city={data?.zipCodeInfo?.City}
						state={data?.zipCodeInfo?.State}
					/>
				</div>

				{data.requireGuardianSignature && (
					<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
						<h2 className="section-heading mb-5 text-lg">
							{__(
								"guardian_signature_label",
								"Parent / Legal Guardian Information",
							)}
						</h2>
						<TextField
							registeredName="gardianSignature.firstName"
							label={__("input_gardian_first_name_label", "First Name")}
							placeholder={__("input_gardian_first_name_label", "First Name")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
						/>
						<TextField
							registeredName="gardianSignature.lastName"
							label={__("input_gardian_last_name_label", "Last Name")}
							placeholder={__("input_gardian_last_name_label", "Last Name")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
						/>
					</div>
				)}

				{data.isLoggedIn && (
					<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
						<h2 className="section-heading mb-5 text-lg">
							{__("member_referral", "Member Referral")}
						</h2>
						<MemberReferralField onSelect={handleReferralSelection} />
					</div>
				)}

				<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
					<h2 className="section-heading mb-5 text-lg">
						{__("emergency_contact", "Emergency Contact")}
					</h2>

					<TextField
						registeredName="emergencyContact.firstName"
						label={__("input_first_name_label", "First Name")}
						placeholder={__("input_first_name_label", "First Name")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
					/>

					<TextField
						registeredName="emergencyContact.lastName"
						label={__("input_last_name_label", "Last Name")}
						placeholder={__("input_last_name_label", "Last Name")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
					/>

					<TelField
						registeredName="emergencyContact.mobilePhone"
						label={__("input_mobile_phone_label", "Mobile Phone")}
						isRequired={true}
						requiredText={__(
							"phone_required",
							"Please enter a valid phone number.",
						)}
						placeholder={__("input_mobile_phone_label", "Mobile Phone")}
					/>
				</div>

				<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
					<h2 className="section-heading mb-5 text-lg">
						{__("set_username_and_password", "Set Username and Password")}
					</h2>
					<TextField
						registeredName="login.userName"
						label={__("input_username_label", "Username")}
						placeholder={__("input_username_label", "Username")}
						description={__("input_username_description", "email recommended")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						minLength={6}
					/>
					<PasswordField
						registeredName="login.password"
						label={__("input_password_label", "Password")}
						placeholder={__("input_password_label", "Password")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						onBlur={async () => {
							await trigger("login.password");
						}}
						minLength={6}
						confirmMatch={true}
					/>
					<PasswordField
						registeredName="login.passwordConfirm"
						label={__("input_confirm_password_label", "Confirm Password")}
						placeholder={__("input_confirm_password_label", "Confirm Password")}
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						matchingFieldKey="login.password"
						onBlur={async () => {
							await trigger("login.passwordConfirm");
						}}
						minLength={6}
						confirmMatch={true}
					/>
				</div>

				{stepData?.get_max_submembers_by_membership_type?.MaxSubmembers > 0 && (
					<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2 pb-4">
						<h2 className="section-heading mb-5 text-lg">
							{__("add_members", "Add Members")}
						</h2>
						{submemberAgeMessage && <InfoNote message={submemberAgeMessage} />}
						<SubMembersForm
							memberEmail={memberEmail}
							maxSubmembers={
								stepData?.get_max_submembers_by_membership_type?.MaxSubmembers
							}
							relationshipOptions={
								stepData?.get_submember_relationships?.Submember_Relationships
							}
							genders={stepData?.get_genders?.Genders}
							isLoading={loading}
						/>
					</div>
				)}

				<div className="mb-10 w-full max-w-96 border border-gray-400 px-3 py-2">
					<h2 className="section-heading mb-5 text-lg">
						{__("membership_agreement", "Membership Agreement")}
					</h2>

					<ContentModal
						isOpen={isTermsModalOpen}
						closeModal={() => {
							setIsTermsModalOpen(false);
						}}
						contentLabel={__(
							"input_membership_agreement_label",
							"Membership Agreement",
						)}
						customStyles={{
							content: {
								bottom: "auto",
								height: "86vh",
								width: "86vw",
								left: "50%",
								position: "fixed",
								right: "auto",
								top: "50%",
								transform: "translate(-50%,-50%)",
								padding: "0",
								paddingLeft: "2rem",
								paddingTop: "1rem",
								borderRadius: "0",
							},
						}}
					>
						<form onSubmit={handleSubmit(onSubmit)} className="mb-2 w-full">
							{data?.selectedClub?.State === "QC" && (
								<div className="w-44">
									<RadioGroupField
										registeredName="member.contractLanguage"
										isRequired={false}
										label={__("input_select_language_label", "Select Language")}
										isClustered={true}
										radios={[
											{
												id: "contractLanguageEn",
												label: "En",
												value: "en",
											},
											{
												id: "contractLanguageFr",
												label: "Fr",
												value: "fr",
											},
										]}
									/>
								</div>
							)}
						</form>
						<iframe
							title={__(
								"membership_terms_and_conditions",
								"Membership Terms and Conditions",
							)}
							width="100%"
							height="90%"
							srcDoc={
								selectedLanguage && selectedLanguage === "fr"
									? data?.get_contract_fr?.MembershipContractContent
									: data?.get_contract?.MembershipContractContent
							}
						/>
					</ContentModal>

					<CheckboxField
						registeredName="member.hasConfirmedContract"
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						label=""
						onChange={(event) => {
							setData({
								...data,
								agreeToTos: event.target.checked,
							});
						}}
					>
						<>
							{__("terms_and_conditions_text", "I agree to the")}{" "}
							<Button
								cssClass="underline hover:no-underline bg-transparent text-black normal-case p-0 border-none inline appearance-none tracking-normal"
								action={() => {
									setIsTermsModalOpen(true);
								}}
							>
								{__(
									"terms_and_conditions_link_text",
									"membership terms and conditions",
								)}
							</Button>
						</>
					</CheckboxField>

					<CheckboxField
						registeredName="member.hasConfirmedUnderstanding"
						isRequired={true}
						requiredText={__("input_required", "This field is required.")}
						label={`${__(
							"dues_confirmation_before_price",
							"I understand that the monthly dues are",
						)} $${
							data?.get_membership_details?.MembershipDetails?.Membership?.Rate
						} ${__(
							"dues_confirmation_after_price",
							"and the dues plus other personal charges will be charged to my account with the payment details I have provided.",
						)}`}
						onChange={(event) => {
							setData({
								...data,
								agreeToDues: event.target.checked,
							});
						}}
					/>
				</div>

				<Button buttonType="submit" isLoading={isLoading}>
					{__("button_text_continue_to_payment", "Continue to Payment")}
				</Button>
			</form>
		</>
	);
};

/**
 * Sub-Member Form Component.
 */
const SubMembersForm = ({
	memberEmail,
	maxSubmembers,
	relationshipOptions,
	genders,
	isLoading,
}: {
	memberEmail: string;
	maxSubmembers: Number;
	relationshipOptions: Array<string>;
	genders: Array<string>;
	isLoading: boolean;
}) => {
	const { control } = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "submembers",
	});
	const [relationships, updateRelationships] = useState({
		value: "",
		index: 0,
	});

	return (
		<>
			{fields.map((field, index) => {
				return (
					<div key={index} className="mb-5 w-full max-w-96 pb-2">
						<div className="text-right">
							<Button
								action={() => remove(index)}
								cssClass="text-xs hover:underline bg-transparent text-black normal-case p-0 mb-3 border-none inline appearance-none tracking-normal"
							>
								{__("add_member_remove_text", "Remove x")}
							</Button>
						</div>

						<TextField
							registeredName={`submembers.${index}.firstname`}
							label={__("input_first_name_label", "First Name")}
							placeholder={__("input_first_name_label", "First Name")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
						/>

						<TextField
							registeredName={`submembers.${index}.lastname`}
							label={__("input_last_name_label", "Last Name")}
							placeholder={__("input_last_name_label", "Last Name")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
						/>

						<SelectField
							registeredName={`submembers.${index}.relationship`}
							label={__("input_relationship_label", "Relationship")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
							options={relationshipOptions?.map((option, i) => ({
								key: i.toString(),
								value: option,
								text: option,
							}))}
							onChange={(event) =>
								updateRelationships({ value: event.target.value, index })
							}
						/>

						<EmailField
							registeredName={`submembers.${index}.email`}
							label={__("input_email_label", "Email")}
							requiredText={__(
								"email_required",
								"Please enter a valid email address.",
							)}
							placeholder={__("input_email_label", "Email")}
							isRequired={
								index === relationships.index && relationships.value !== "Child"
							}
							memberEmail={memberEmail}
						/>

						<TelField
							registeredName={`submembers.${index}.mobilenumber`}
							label={__("input_mobile_phone_label", "Mobile Phone")}
							requiredText={__(
								"phone_required",
								"Please enter a valid phone number.",
							)}
							placeholder={__("input_mobile_phone_label", "Mobile Phone")}
							isRequired={
								index === relationships.index && relationships.value !== "Child"
							}
						/>

						<DateField
							registeredName={`submembers.${index}.dateOfBirth`}
							label={__("input_dob_label", "DOB")}
							isRequired={true}
							requiredText={__("input_required", "This field is required.")}
							placeholder={__("input_dob_placeholder", "1990-12-12")}
						/>

						{isLoading ? (
							<Loader colorCircle="#000" colorRotator="#ddd" />
						) : (
							<RadioGroupField
								registeredName={`submembers.${index}.gender`}
								label={__("input_gender_label", "Gender")}
								isRequired={true}
								requiredText={__("input_required", "This field is required.")}
								radios={genders.map((gender) => ({
									id: `submembers-${index}-gender-${gender}`,
									label: gender,
									value: gender,
								}))}
							/>
						)}
					</div>
				);
			})}

			{/* Add a Member Button */}
			{fields.length < Number(maxSubmembers) && (
				<Button
					action={() => {
						append({
							firstname: "",
							lastname: "",
							email: "",
							relationship: "",
							dateOfBirth: "",
							gender: "",
							mobilenumber: "",
						});
					}}
					buttonType="button"
				>
					{__("button_text_add_member", "+ Add a Member")}
				</Button>
			)}
		</>
	);
};

export default StepDetails;
